import { Injectable } from '@angular/core';
import { LookbookApiService } from '@epicuro-next/data/engagement/engagement-api';
import {
  EntityType,
  NewsfeedType,
} from '@epicuro-next/data/engagement/engagement-models';
import { LookBook } from '@epicuro-next/models/engagement';
import { APP_PRIVATE_PATHS } from '@epicuro-next/navigation/constants';
import { actions as feedActions } from '@epicuro-next/state/news-feed-state';
import { go } from '@epicuro-next/state/router-store';
import {
  selectAccountId,
  selectSiteId,
} from '@epicuro-next/state/session-state';
import { MessageType, showSnackBarMessage } from '@epicuro-next/state/snackbar';
import { isNonNullish } from '@epicuro-next/utilities';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { select, Store } from '@ngrx/store';
import { of } from 'rxjs';
import {
  catchError,
  concatMap,
  filter,
  map,
  switchMap,
  take,
  withLatestFrom,
} from 'rxjs/operators';

import { selectLookBookInRoute } from '../core';
import { selectLookBooks } from '../shop-the-looks';
import { adaptLookBookToSend, shopTheLookPath } from '../utils';

import {
  clearShopTheLookEditState,
  deleteLookBook,
  deleteLookBookError,
  deleteLookBookNotify,
  deleteLookBookSuccess,
  editLookBook,
  editLookBookError,
  editLookBookNotify,
  editLookBookSuccess,
  loadLookBookEdit,
  loadLookBookEditError,
  loadLookBookEditSuccess,
} from './edit-shop-the-look-actions';

@Injectable()
export class EditShopTheLookEffects {
  constructor(
    private actions$: Actions,
    private lookbookApi: LookbookApiService,
    private store: Store,
  ) {}

  public loadLookBookToEditIfDefined$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadLookBookEdit),
      map(({ lookBookId }) => lookBookId),
      filter(isNonNullish),
      concatLatestFrom(() => [
        this.store.pipe(select(selectAccountId), filter(isNonNullish)),
        this.store.select(selectSiteId).pipe(filter(isNonNullish)),
      ]),
      switchMap(([lookBookId, accountId, siteId]) =>
        this.lookbookApi
          .getLookBook({
            queryparams: { lookBookId },
            accountId,
            siteId: siteId ?? '',
          })
          .pipe(
            take(1),
            map((lookBook) => {
              if (lookBook) {
                return loadLookBookEditSuccess({
                  lookBook: lookBook as unknown as LookBook,
                });
              } else {
                return loadLookBookEditError({
                  error: 'Not found',
                });
              }
            }),
            catchError((err) => [
              loadLookBookEditError({
                error: err.message,
              }),
            ]),
          ),
      ),
    ),
  );

  public editLookBook$ = createEffect(() =>
    this.actions$.pipe(
      ofType(editLookBook),
      switchMap(({ lookBook }) =>
        this.lookbookApi.editLookBook(adaptLookBookToSend(lookBook)).pipe(
          switchMap(() => {
            return [
              editLookBookSuccess({ lookBook }),
              clearShopTheLookEditState(),
              editLookBookNotify({
                message: `${lookBook.title ?? 'LookBook'} created.`,
              }),
            ];
          }),
          catchError((error) =>
            of(editLookBookError({ error: error.message })),
          ),
        ),
      ),
    ),
  );

  public renameArticleOnLookBookSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(editLookBookSuccess),
      map(({ lookBook: { newsfeedId, title } }) => ({ newsfeedId, title })),
      filter(({ newsfeedId }) => !!newsfeedId),
      map(({ newsfeedId, title }) =>
        feedActions.renameArticle({ id: newsfeedId as string, title }),
      ),
    ),
  );

  public editLookBookSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(editLookBookSuccess),
      map((action) =>
        go({
          commands: ['browse', 'shop-the-look', action.lookBook.id],
        }),
      ),
    ),
  );

  public deleteLookBook$ = createEffect(() =>
    this.actions$.pipe(
      ofType(deleteLookBook),
      concatMap((action) =>
        of(action.lookBookId).pipe(
          withLatestFrom(this.store.pipe(select(selectLookBookInRoute))),
          switchMap(([lookBookId, deleteLookbook]) =>
            this.lookbookApi.deleteLookBook(lookBookId).pipe(
              map(() => {
                const lookbookTitle = deleteLookbook?.title;
                const newsfeedId = deleteLookbook?.newsfeedId;
                return deleteLookBookSuccess({
                  lookBookId,
                  lookbookTitle,
                  lookbookNewsFeedId: newsfeedId as string,
                });
              }),
              catchError((error) =>
                of(deleteLookBookError({ error: error.message })),
              ),
            ),
          ),
        ),
      ),
    ),
  );

  public deleteLookbookSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(deleteLookBookSuccess),
      concatMap((action) =>
        of(action).pipe(
          withLatestFrom(this.store.pipe(select(selectLookBooks))),
          map(([, lookbooks]) => {
            const first = lookbooks && lookbooks.length ? lookbooks[0] : null;

            if (!first) {
              return go({
                commands: [
                  APP_PRIVATE_PATHS.browse.path,
                  APP_PRIVATE_PATHS.search.path,
                ],
              });
            }

            return go({
              commands: shopTheLookPath([first.id]),
            });
          }),
        ),
      ),
    ),
  );

  public removeLookbookFromFeed$ = createEffect(() =>
    this.actions$.pipe(
      ofType(deleteLookBookSuccess),
      filter(
        ({ lookbookNewsFeedId, lookBookId }) =>
          isNonNullish(lookBookId) && isNonNullish(lookbookNewsFeedId),
      ),
      map(({ lookBookId, lookbookNewsFeedId }) =>
        feedActions.deleteArticleSuccess({
          feedId: (lookbookNewsFeedId as unknown as string) ?? '0',
          referenceId: lookBookId,
          referenceType: EntityType.LookBook,
          newsfeedType: NewsfeedType.Spotlight,
        }),
      ),
    ),
  );

  public showSnackBarDeletedLookbook$ = createEffect(() =>
    this.actions$.pipe(
      ofType(deleteLookBookNotify),
      map(({ lookbookTitle }) => {
        const message = $localize`:@@DeleteLookbookSnackMessage:${
          lookbookTitle ?? 'Lookbook'
        } deleted`;
        return showSnackBarMessage({
          message,
          messageType: MessageType.SUCCESS,
        });
      }),
    ),
  );
}
